import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { clearRecent, getRecent, getRecentDocuments, onRecentChange } from '../lib/recent';
import type { RecentDocument, RecentEntry } from '../lib/recent';
import { formatRelative } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import type { IconName } from '../components/ui/Icon';
import { FilePlate } from '../components/FilePlate';
import { GlobalSearch } from '../components/GlobalSearch';
import { UploadDestinationDialog } from '../components/UploadDestinationDialog';

const KIND_LABEL = { customer: 'Customer', site: 'Job', supplier: 'Supplier' } as const;
const KIND_ICON: Record<RecentEntry['kind'], IconName> = { customer: 'building', site: 'briefcase', supplier: 'truck' };

function hasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

export function HomePage() {
  const [recent, setRecent] = useState<RecentEntry[]>([]);
  const [recentDocs, setRecentDocs] = useState<RecentDocument[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = () => {
      setRecent(getRecent());
      setRecentDocs(getRecentDocuments());
    };
    load();
    return onRecentChange(load);
  }, []);

  const onPicked = (e: ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (list.length > 0) setFiles(list);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const list = Array.from(e.dataTransfer.files);
    if (list.length > 0) setFiles(list);
  };

  return (
    <div className="mx-auto max-w-6xl">
      <section className="pt-2 lg:pt-8" aria-labelledby="home-title">
        <h1 id="home-title" className="text-5xl leading-none lg:text-6xl">
          Files
        </h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          Everything filed against Oracle customers, jobs and suppliers. Search for a record, paste its Attachments
          link, or start with the files and pick where they go.
        </p>
        <div className="gsl-search-hero mt-5">
          <GlobalSearch autoFocus scopeInline />
        </div>
      </section>

      <section className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]" aria-label="Upload and recent activity">
        <div className="space-y-6">
          <div
            className="gsl-dropzone gsl-dropzone-hero"
            data-active={dragging ? 'true' : 'false'}
            onDragEnter={(e) => {
              if (hasFiles(e)) setDragging(true);
            }}
            onDragOver={(e) => {
              if (hasFiles(e)) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
            }}
            onDrop={onDrop}
          >
            <input ref={inputRef} type="file" multiple className="sr-only" onChange={onPicked} aria-label="Choose files to upload" />
            <span className="gsl-dropzone-icon">
              <Icon name="upload" size={26} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-base font-medium">Drop files here to upload</p>
              <p className="text-sm text-muted-foreground">
                PDF, JPG, PNG, HEIC, TIFF, XLSX or DOCX. You pick the customer, job or supplier next, then the folder.
              </p>
            </div>
            <Button variant="secondary" icon="upload" onClick={() => inputRef.current?.click()}>
              Choose files
            </Button>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-xl leading-none">Folders</h2>
              <span className="flex items-center gap-1">
                <Link to="/browse" className="gsl-btn gsl-btn-secondary gsl-btn-sm">
                  <Icon name="folder" size={15} /> Browse all
                </Link>
              {recent.length > 0 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    clearRecent();
                    setRecent([]);
                    setRecentDocs([]);
                  }}
                >
                  Clear history
                </Button>
              ) : null}
              </span>
            </div>
            {recent.length === 0 ? (
              <div className="grid gap-3 sm:grid-cols-3">
                <HelpTile icon="building" title="Customers">
                  Credit, compliance, legal and other documents, with each job underneath.
                </HelpTile>
                <HelpTile icon="briefcase" title="Jobs">
                  Preliminary notices, lien releases, job information and bid requirements.
                </HelpTile>
                <HelpTile icon="truck" title="Suppliers">
                  W-9s, TIN matches, banking updates, insurance and contracts.
                </HelpTile>
              </div>
            ) : (
              <ul className="grid gap-3 sm:grid-cols-2">
                {recent.map((r) => (
                  <li key={r.href}>
                    <Link to={r.href} className="gsl-folder-card">
                      <span className="gsl-folder-card-icon">
                        <Icon name="folder" size={22} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{r.label}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          <Icon name={KIND_ICON[r.kind]} size={11} className="mr-1 inline-block align-[-1px]" />
                          {KIND_LABEL[r.kind]} <span className="gsl-num">{r.number}</span> · {formatRelative(r.visitedAt)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          <h2 className="mb-3 text-xl leading-none">Recent files</h2>
          {recentDocs.length === 0 ? (
            <div className="gsl-panel flex flex-col items-center gap-2 px-6 py-10 text-center">
              <Icon name="file" size={26} className="text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Files you open will show up here.</p>
            </div>
          ) : (
            <ul className="gsl-panel divide-y divide-border">
              {recentDocs.map((d) => (
                <li key={d.href}>
                  <Link to={d.href} className="flex items-center gap-3 px-3 py-2.5 no-underline hover:bg-surface-raised">
                    <FilePlate filename={d.filename} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{d.title}</span>
                      <span className="block truncate text-xs text-muted-foreground">{d.record ?? d.filename ?? ''}</span>
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">{formatRelative(d.visitedAt)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <UploadDestinationDialog open={files.length > 0} files={files} onClose={() => setFiles([])} />
    </div>
  );
}

function HelpTile({ icon, title, children }: { icon: IconName; title: string; children: string }) {
  return (
    <div className="gsl-panel p-4">
      <span className="gsl-tile-icon">
        <Icon name={icon} size={18} />
      </span>
      <h3 className="mt-3 font-sans text-sm font-semibold tracking-normal">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{children}</p>
    </div>
  );
}
